import {Globals} from '../../../globals';
import {isProfileUrl} from '../../../shared/listing-route.utils';
import {formatBrowseSegmentLabel} from '../../../shared/browse-seo.utils';
import {FILTER_CHAIN} from './category-level.config';

const ADD_LISTING_PLATFORM_SLUGS = new Set([
  'add-listing', 'add-item', 'update-item', 'home', 'item', 'items',
  'login', 'profile', 'signup', 'payment', 'messaging', 'compare', 'admin', 'account',
]);

/** Params for add-listing category picker APIs — taxonomy only, never profile or route slugs. */
export function buildAddListingCategoryApiParams(globals: Globals): Record<string, string> {
  const params: Record<string, string> = {};
  const keys = ['category', ...FILTER_CHAIN];
  for (const key of keys) {
    const value = `${globals.listingFilter[key as keyof typeof globals.listingFilter] ?? ''}`.trim();
    if (!value || value === 'null' || ADD_LISTING_PLATFORM_SLUGS.has(value.toLowerCase())) {
      continue;
    }
    params[key] = value;
  }
  return params;
}

/** Params for add-listing category picker APIs — derived from URL, not shared globals. */
export function buildAddListingCategoryApiParamsFromUrl(
  urlSegments: string[],
): Record<string, string> {
  const params: Record<string, string> = {};
  const start = urlSegments[0] === 'add-listing' ? 1 : 0;

  const category = urlSegments[start]?.replace(/^all-/, '');
  if (category && !ADD_LISTING_PLATFORM_SLUGS.has(category.toLowerCase())) {
    params.category = category;
  }

  const taxonomyStart = start + 1;
  for (let i = 0; i < FILTER_CHAIN.length; i++) {
    const segment = urlSegments[taxonomyStart + i];
    if (!segment || segment === 'all') {
      break;
    }
    const slug = segment.startsWith('all-') ? segment.slice(4) : segment;
    if (ADD_LISTING_PLATFORM_SLUGS.has(slug.toLowerCase())) {
      break;
    }
    params[FILTER_CHAIN[i]] = slug;
  }

  return params;
}

/** Browse picker depth implied by URL (2 = /category/sub, 3 = /category/sub/subsub, …). */
export function browseCategoryPickerDepth(urlSegments: string[]): number {
  const profile = isProfileUrl(urlSegments);
  const offset = profile ? 1 : 0;
  let taxonomySegments = 0;
  for (let i = offset + 1; i < urlSegments.length; i++) {
    const segment = urlSegments[i];
    if (!segment || segment === 'all') {
      break;
    }
    taxonomySegments++;
  }
  return taxonomySegments + 1;
}

/** Add-listing picker depth (2 = /add-listing/test3, 3 = /add-listing/test3/wow, …). */
export function addListingCategoryPickerDepth(urlSegments: string[]): number {
  const start = urlSegments[0] === 'add-listing' ? 1 : 0;
  let taxonomySegments = 0;
  for (let i = start; i < urlSegments.length; i++) {
    const segment = urlSegments[i];
    if (!segment || segment === 'all') {
      break;
    }
    taxonomySegments++;
  }
  return taxonomySegments + 1;
}

/** Taxonomy API params for browse category pickers — derived from URL, not shared globals. */
export function buildBrowseCategoryApiParams(
  urlSegments: string[],
  pickerDepth: number,
): Record<string, string> {
  const params: Record<string, string> = {};
  const profile = isProfileUrl(urlSegments);
  const offset = profile ? 1 : 0;

  if (profile) {
    params.profile = urlSegments[0];
  }

  const category = urlSegments[offset]?.replace(/^all-/, '');
  if (category) {
    params.category = category;
  }

  const parentCount = Math.max(pickerDepth - 1, 0);
  for (let i = 0; i < parentCount && i < FILTER_CHAIN.length; i++) {
    const segment = urlSegments[offset + 1 + i];
    if (!segment || segment === 'all') {
      break;
    }
    params[FILTER_CHAIN[i]] = segment;
  }

  return params;
}

function slugSegment(value: string): string {
  return value
    .replace(/ & /g, '-and-')
    .replace(/ /g, '-');
}

function filterValue(globals: Globals, key: string): string {
  return `${globals.listingFilter[key as keyof typeof globals.listingFilter] ?? ''}`;
}

function normalizeProfileId(id: string | undefined): string | undefined {
  if (!id) {
    return undefined;
  }
  const trimmed = id.replace(/^\//, '').trim();
  return trimmed || undefined;
}

function itemSlug(item: any[]): string {
  return String(item[1] ?? item[0] ?? '').replace(/ /g, '-');
}

export function isCategoryModule(item: any[]): boolean {
  return !isCategoryComponent(item) && item[3] === 'module';
}

export function isCategoryComponent(item: any[]): boolean {
  return item[4] === 'component' || item[3] === 'component';
}

export function categoryItemCountLabel(item: any[]): string {
  const raw = item?.[5] ?? item?.[4];
  if (raw == null || raw === '') {
    return '0 items';
  }
  const n = Number(raw);
  if (Number.isNaN(n)) {
    return '0 items';
  }
  return n === 1 ? '1 item' : `${n} items`;
}

/** Motors-only: first module at depth 2 may use an "All …" listing shortcut. */
export function shouldShowAllCard(
  depth: number,
  index: number,
  item: any[],
  baseCategory: string,
): boolean {
  return baseCategory === 'motors' && depth === 2 && index === 0 && isCategoryModule(item);
}

export function categoryAllCardTitle(item: any[]): string {
  const label = formatBrowseSegmentLabel(String(item[1] ?? item[0] ?? ''));
  if (!label) {
    return 'All';
  }
  return `All ${label}`;
}

/** Human-readable label from a taxonomy row; never prefixes "All". */
export function categoryDisplayTitle(item: any[]): string {
  let name = String(item[0] ?? '').trim();
  if (name && name !== 'auto' && name !== 'vehicle' && !/^all[\s-]+/i.test(name)) {
    return formatBrowseSegmentLabel(name);
  }
  if (/^all[\s-]+/i.test(name)) {
    name = name.replace(/^all[\s-]+/i, '');
    if (name) {
      return formatBrowseSegmentLabel(name);
    }
  }
  return formatBrowseSegmentLabel(String(item[1] ?? '').trim());
}

/** Builds browse link from a base-category landing page (e.g. /smartphone → /smartphone/used/all). */
export function buildBrowseSubcategoryLink(
  profileId: string | null | undefined,
  baseCategory: string,
  item: any[],
): any[] {
  const segments: string[] = [];
  if (profileId) {
    segments.push(profileId);
  }
  segments.push(baseCategory || 'home');
  const slug = itemSlug(item);
  if (isCategoryModule(item)) {
    segments.push(slug);
  } else {
    segments.push(slug, 'all');
  }
  return ['/', ...segments];
}

/** Builds absolute routerLink segments for browse category cards. */
export function buildBrowseCategoryLink(
  id: string | undefined,
  globals: Globals,
  depth: number,
  item: any[],
  isAllCard: boolean,
  baseCategory: string,
): any[] {
  const segments: string[] = [];
  const profileId = normalizeProfileId(id);
  if (profileId) {
    segments.push(profileId);
  }
  segments.push(baseCategory || 'home');

  const slug = itemSlug(item);

  if (depth === 2) {
    const subcategory = slugSegment(filterValue(globals, 'subcategory'));
    if (subcategory) {
      segments.push(subcategory);
    }
    if (isAllCard) {
      segments.push(slug, 'all');
    } else if (isCategoryModule(item)) {
      segments.push(slug);
    } else {
      segments.push(slug, 'all');
    }
    return ['/', ...segments];
  }

  const pathFilters = FILTER_CHAIN.slice(0, depth - 1);
  pathFilters.forEach(key => {
    const value = filterValue(globals, key);
    if (value && value !== 'null') {
      segments.push(slugSegment(value));
    }
  });

  segments.push(slug);
  if (isAllCard || !isCategoryModule(item)) {
    segments.push('all');
  }
  return ['/', ...segments];
}

/** @deprecated Use buildBrowseCategoryLink */
export const buildBrowseMotorsLink = buildBrowseCategoryLink;

/** Builds absolute routerLink for add-listing category cards. */
export function buildAddItemLink(
  currentUrl: string,
  item: any[],
  _isAllCard: boolean,
  _baseCategory: string,
): any[] {
  const slug = String(item[1] ?? item[0] ?? '').replace(/ /g, '-');
  if (!slug) {
    return [];
  }
  const pathOnly = currentUrl.split(/[?#]/)[0];
  const segments = pathOnly.split('/').filter(Boolean);

  if (isCategoryModule(item)) {
    return ['/', ...segments, slug];
  }
  // Component leaf, or rows without module/component metadata (item-count browse rows).
  return ['/', ...segments, slug, 'all'];
}

export function getGroupTitle(globals: Globals, titleFilterKey: string): string {
  const value = filterValue(globals, titleFilterKey);
  return value && value !== 'null' ? value : '';
}

/** Add-listing category picker: API depth follows URL segments, not route depth alone. */
export function addListingItemsApiPath(url: string): string {
  const segments = url.split(/[?#]/)[0].split('/').filter(Boolean);
  const start = segments[0] === 'add-listing' ? 1 : 0;
  const subs = Math.max(segments.length - start, 1);
  return `/items/${'sub'.repeat(subs)}categories`;
}
